// src/components/ClientWhatsappButton.js
import React, { useState, useEffect } from 'react';
import { User, Calendar, Key, X, Clock } from 'lucide-react';

// Componente para el icono de WhatsApp (algunos navegadores no tienen el icono en Lucide)
export const BrandWhatsapp = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 24}
      height={props.size || 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
    >
      <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
      <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M9.5 13.5c.5 1 1.5 2 2.5 2s2-1 2.5-2" />
    </svg>
  );
};

const ClientWhatsappButton = ({ client, accounts, services }) => {
  const [showProfilesModal, setShowProfilesModal] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [templates, setTemplates] = useState({});
  const [showNotificationTypeModal, setShowNotificationTypeModal] = useState(false);
  const [selectedNotificationType, setSelectedNotificationType] = useState('');
  const [profilesForNotification, setProfilesForNotification] = useState([]);

  // Cargar plantillas al iniciar
  useEffect(() => {
    const savedTemplates = localStorage.getItem('whatsapp-notification-templates');
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Plantillas por defecto en caso de que no haya guardadas
  const defaultTemplates = {
    expirado: `🚨 PERFIL EXPIRADO 🚨
----------------------------
Hola {{nombre_cliente}}, 

Tu perfil de {{servicio}} "{{perfil}}" ha EXPIRADO el {{fecha_expiracion}}.

Para renovar tu suscripción:
💰 Precio: {{precio}}
📱 Contáctanos para reactivar

⚠️ Tu acceso ha sido suspendido
📝 Información adicional: {{observaciones}}
📩 Clover - Tu proveedor de confianza`,

    renovaciones: `📅 RECORDATORIO DE RENOVACIÓN 📅
----------------------------
Hola {{nombre_cliente}},

Tu perfil de {{servicio}} "{{perfil}}" vence el {{fecha_expiracion}}.

Detalles de renovación:
💰 Precio: {{precio}}
⏰ Quedan {{dias_restantes}} días
🔄 Renueva ahora para no perder acceso

📝 Notas: {{observaciones}}
📩 Clover - Siempre contigo`,

    cambio_contraseña: `🔐 CAMBIO DE CONTRASEÑA 🔐
----------------------------
Hola {{nombre_cliente}},

Se ha actualizado la información de acceso de tu perfil "{{perfil}}" en {{servicio}}.

NUEVA INFORMACIÓN:
📧 Usuario: {{usuario}}
🔑 Nueva Contraseña: {{nueva_contraseña}}
👤 Perfil: {{perfil}}
🔢 PIN: {{pin}}

📝 Información adicional: {{observaciones}}
⚠️ Guarda esta información en un lugar seguro
📩 Clover - Soporte 24/7`,

    garantia: `🛡️ SOPORTE Y GARANTÍA 🛡️
----------------------------
Hola {{nombre_cliente}},

Estamos aquí para ayudarte con tu servicio {{servicio}}.

Información de tu cuenta:
👤 Perfil: {{perfil}}
📧 Usuario: {{usuario}}
🔢 PIN: {{pin}}
📅 Vence: {{fecha_expiracion}}

📝 Detalles de cuenta: {{observaciones}}
🔧 Problemas técnicos: Solucionados en 24h
🔄 Reemplazo de cuenta: Disponible
💬 Soporte: Respuesta inmediata

📩 Clover - Tu satisfacción es nuestra prioridad`
  };
  
  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // ClientWhatsappButton.js - Mejorar la función getClientProfiles
  const getClientProfiles = () => {
    // Si el cliente ya tiene los perfiles precargados, úsalos
    if (client.profiles && client.profiles.length > 0) {
      console.log(`Cliente ${client.name} tiene ${client.profiles.length} perfiles precargados`);
      return client.profiles.map(profile => ({
        ...profile,
        accountId: profile.account_id,
        username: profile.username,
        password: profile.password,
        serviceId: profile.service_id,
        serviceName: profile.service_name,
        servicePrice: profile.service_price,
        expiryDate: profile.expiry_date,
        name: profile.profile_name,
        id: profile.profile_id
      }));
    }
    
    // Fallback al método anterior si no hay datos precargados
    if (!accounts || !client) return [];
    
    const clientProfiles = [];
    
    accounts.forEach(account => {
      if (account.profiles) {
        account.profiles.forEach(profile => {
          // Verificar tanto client_id como clientId para compatibilidad
          const profileClientId = profile.client_id || profile.clientId;
          if (profileClientId === client.id && profile.status === 'ocupado') {
            const serviceId = account.service_id || account.serviceId;
            const service = services?.find(s => s.id === parseInt(serviceId));
            
            const expiryDate = profile.expiryDate || profile.expiry_date;
            
            clientProfiles.push({
              ...profile,
              accountId: account.id,
              username: account.username,
              password: account.password,
              serviceId: serviceId,
              serviceName: service ? service.name : 'N/A',
              servicePrice: service ? service.price : 0,
              expiryDate: expiryDate,
              expiry_date: expiryDate
            });
          }
        });
      }
    });
    
    console.log(`Cliente ${client.name} tiene ${clientProfiles.length} perfiles activos (fallback)`);
    return clientProfiles;
  };
  
  // Verificar si un perfil está próximo a expirar (menos de 5 días)
  const isProfileNearExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    // Calcular la diferencia en días
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 && diffDays <= 5;
  };
  
  // Verificar si un perfil ha expirado
  const isProfileExpired = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    return expiryDate < today;
  };

  // Calcular días hasta expiración o desde expiración
  const getDaysFromExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return 0;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  // Formatear fecha con manejo mejorado
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Intentar convertir a fecha válida
    const date = new Date(dateString);
    
    // Verificar si es una fecha válida
    if (isNaN(date.getTime())) {
      console.log('Fecha inválida:', dateString);
      return 'N/A';
    }
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  
  // Obtener días hasta expiración
  const getDaysUntilExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return 'N/A';
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    // Si ya expiró
    if (expiryDate < today) {
      const expiredDays = Math.ceil((today - expiryDate) / (1000 * 60 * 60 * 24));
      return `Expirado hace ${expiredDays} día${expiredDays !== 1 ? 's' : ''}`;
    }
    
    // Calcular la diferencia en días
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} día${diffDays !== 1 ? 's' : ''}`;
  };

  // Función para reemplazar variables en las plantillas - ACTUALIZADA
  const replaceTemplateVariables = (template, profile, notificationType = 'renovaciones') => {
    if (!template) return '';

    const daysFromExpiry = getDaysFromExpiry(profile);
    const serviceName = profile.serviceName || 'N/A';
    const formattedPrice = formatCurrency(profile.servicePrice);
    
    // Obtener las observaciones de la cuenta asociada al perfil
    const getAccountObservations = (profile) => {
      if (!accounts || !profile.accountId) return 'Sin observaciones';
      
      const account = accounts.find(acc => acc.id === profile.accountId);
      return account?.observation || 'Sin observaciones';
    };
    
    const replacements = {
      '{{nombre_cliente}}': client.name || 'Cliente',
      '{{servicio}}': serviceName,
      '{{perfil}}': profile.name || 'Perfil',
      '{{usuario}}': profile.username || 'N/A',
      '{{precio}}': formattedPrice,
      '{{pin}}': profile.pin || 'N/A',
      '{{telefono}}': client.phone || 'N/A',
      '{{fecha_expiracion}}': formatDate(profile.expiryDate || profile.expiry_date),
      '{{dias_restantes}}': Math.max(0, daysFromExpiry).toString(),
      '{{dias_expirado}}': Math.max(0, -daysFromExpiry).toString(),
      '{{nueva_contraseña}}': profile.password || 'N/A',
      '{{contraseña_anterior}}': 'Contraseña anterior',
      '{{tipo_problema}}': 'Problema reportado',
      '{{fecha_reporte}}': formatDate(new Date()),
      '{{observaciones}}': getAccountObservations(profile)  // NUEVA VARIABLE AGREGADA
    };

    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return replacements[match] || match;
    });
  };

  // Crear enlace de WhatsApp con plantilla personalizada - VERSIÓN MEJORADA
  const createCustomWhatsAppLink = (profiles, notificationType) => {
    if (!profiles || profiles.length === 0) return '';
    
    // Formatear el número de teléfono
    const phone = client.phone.replace(/\s+/g, '').replace(/-/g, '');
    
    // Obtener la plantilla correspondiente
    const template = templates[notificationType] || defaultTemplates[notificationType] || '';
    
    let message = '';

    if (profiles.length === 1) {
      // Un solo perfil
      message = replaceTemplateVariables(template, profiles[0], notificationType);
    } else {
      // Múltiples perfiles - crear mensaje personalizado más limpio
      message = `Hola ${client.name},\n\n`;
      
      if (notificationType === 'expirado') {
        message += `🚨 Tienes ${profiles.length} perfiles EXPIRADOS:\n`;
        message += `═══════════════════════════\n\n`;
      } else if (notificationType === 'renovaciones') {
        message += `📅 Tienes ${profiles.length} perfiles próximos a vencer:\n`;
        message += `═══════════════════════════\n\n`;
      } else if (notificationType === 'cambio_contraseña') {
        message += `🔐 Información actualizada de ${profiles.length} perfiles:\n`;
        message += `═══════════════════════════\n\n`;
      } else {
        message += `🛡️ Información de soporte para ${profiles.length} perfiles:\n`;
        message += `═══════════════════════════\n\n`;
      }

      profiles.forEach((profile, index) => {
        const serviceName = profile.serviceName || 'N/A';
        const formattedPrice = formatCurrency(profile.servicePrice);
        
        message += `📌 PERFIL ${index + 1}\n`;
        message += `🎬 Servicio: ${serviceName}\n`;
        message += `👤 Perfil: ${profile.name || 'Perfil'}\n`;
        message += `📧 Usuario: ${profile.username || 'N/A'}\n`;
        
        if (notificationType === 'cambio_contraseña') {
          message += `🔑 Nueva Contraseña: ${profile.password || 'N/A'}\n`;
        }
        
        if (profile.pin) {
          message += `🔢 PIN: ${profile.pin}\n`;
        }
        
        if (profile.expiryDate || profile.expiry_date) {
          const expiryDate = formatDate(profile.expiryDate || profile.expiry_date);
          if (isProfileExpired(profile)) {
            message += `❌ EXPIRADO el ${expiryDate}\n`;
          } else {
            message += `⏰ Vence el ${expiryDate}\n`;
          }
        }
        
        message += `💰 Precio: ${formattedPrice}\n`;
        
        // Agregar observaciones si están disponibles
        const accountObservations = getAccountObservations(profile);
        if (accountObservations && accountObservations !== 'Sin observaciones') {
          message += `📝 Observaciones: ${accountObservations}\n`;
        }
        
        if (index < profiles.length - 1) {
          message += `\n─────────────────────────\n\n`;
        }
      });

      // Footer único según el tipo de notificación
      message += `\n═══════════════════════════\n`;
      
      if (notificationType === 'expirado') {
        message += `⚠️ Tu acceso ha sido suspendido\n`;
        message += `📱 Contáctanos para reactivar tus servicios\n`;
      } else if (notificationType === 'renovaciones') {
        const totalAmount = profiles.reduce((sum, profile) => sum + parseFloat(profile.servicePrice || 0), 0);
        message += `💳 TOTAL A RENOVAR: ${formatCurrency(totalAmount)}\n`;
        message += `🔄 Renueva ahora para no perder acceso\n`;
      } else if (notificationType === 'cambio_contraseña') {
        message += `⚠️ Guarda esta información en un lugar seguro\n`;
        message += `🔧 Si tienes problemas, contáctanos\n`;
      } else {
        message += `🔧 Problemas técnicos: Solucionados en 24h\n`;
        message += `🔄 Reemplazo de cuenta: Disponible\n`;
        message += `💬 Soporte: Respuesta inmediata\n`;
      }
      
      message += `\n📩 Clover - Tu proveedor de confianza`;
    }
    
    // Crear link de WhatsApp
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  // Función auxiliar para obtener observaciones de la cuenta
  const getAccountObservations = (profile) => {
    if (!accounts || !profile.accountId) return 'Sin observaciones';
    
    const account = accounts.find(acc => acc.id === profile.accountId);
    return account?.observation || 'Sin observaciones';
  };

  // Función para detectar automáticamente el tipo de notificación más apropiado
  const detectNotificationType = (profiles) => {
    if (!profiles || profiles.length === 0) return 'renovaciones';
    
    // Contar perfiles por estado
    const expiredCount = profiles.filter(profile => isProfileExpired(profile)).length;
    const nearExpiryCount = profiles.filter(profile => isProfileNearExpiry(profile)).length;
    
    // Prioridad: Expirados > Próximos a vencer > Renovaciones (default)
    if (expiredCount > 0) {
      return 'expirado';
    } else if (nearExpiryCount > 0) {
      return 'renovaciones';
    } else {
      return 'renovaciones'; // Default para perfiles normales
    }
  };

  // Función para obtener tipos de notificación disponibles según el contexto
  const getAvailableNotificationTypes = (context = 'client') => {
    const allTypes = [
      { key: 'expirado', label: '🚨 Perfil Expirado', color: 'bg-red-900 hover:bg-red-800', description: 'Para perfiles que ya han vencido' },
      { key: 'renovaciones', label: '📅 Renovación', color: 'bg-orange-900 hover:bg-orange-800', description: 'Para perfiles próximos a vencer' },
      { key: 'cambio_contraseña', label: '🔐 Cambio de Contraseña', color: 'bg-blue-900 hover:bg-blue-800', description: 'Para notificar nuevas credenciales' },
      { key: 'garantia', label: '🛡️ Garantía/Soporte', color: 'bg-purple-900 hover:bg-purple-800', description: 'Para brindar soporte técnico' }
    ];

    // En el listado de clientes, solo mostrar expirado y renovaciones
    if (context === 'client') {
      return allTypes.filter(type => ['expirado', 'renovaciones'].includes(type.key));
    }
    
    // En otros contextos (gestión de plantillas), mostrar todos
    return allTypes;
  };

  // Función para mostrar el selector de tipo de notificación
  const showNotificationTypeSelector = (profiles, context = 'client') => {
    setProfilesForNotification(profiles);
    
    // Detectar automáticamente el tipo más apropiado
    const detectedType = detectNotificationType(profiles);
    setSelectedNotificationType(detectedType);
    
    setShowNotificationTypeModal(true);
  };

  // Función para enviar notificación directamente sin modal
  const sendDirectNotification = (profiles, notificationType) => {
    const link = createCustomWhatsAppLink(profiles, notificationType);
    window.open(link, '_blank');
  };

  // Función para enviar notificación del tipo seleccionado
  const sendCustomNotification = () => {
    const link = createCustomWhatsAppLink(profilesForNotification, selectedNotificationType);
    window.open(link, '_blank');
    setShowNotificationTypeModal(false);
    setSelectedNotificationType('');
    setProfilesForNotification([]);
  };
  
  
  // Manejar selección de perfil
  const handleToggleProfileSelection = (profileId) => {
    setSelectedProfiles(prev => ({
      ...prev,
      [profileId]: !prev[profileId]
    }));
  };
  
  // Manejar selección de todos los perfiles
  const handleToggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    const newSelection = {};
    clientProfiles.forEach(profile => {
      newSelection[profile.id] = newSelectAll;
    });
    
    setSelectedProfiles(newSelection);
  };
  
  // Obtener perfiles seleccionados
  const getSelectedProfiles = () => {
    return clientProfiles.filter(profile => selectedProfiles[profile.id]);
  };
  
  // Obtener perfiles por expirar
  const getNearExpiryProfiles = () => {
    return clientProfiles.filter(profile => isProfileNearExpiry(profile));
  };
  
  // Obtener perfiles expirados
  const getExpiredProfiles = () => {
    return clientProfiles.filter(profile => isProfileExpired(profile));
  };
  
  const clientProfiles = getClientProfiles();
  const hasNearExpiryProfiles = clientProfiles.some(profile => isProfileNearExpiry(profile));
  const hasExpiredProfiles = clientProfiles.some(profile => isProfileExpired(profile));
  
  return (
    <>
      <button 
        className={`text-green-400 hover:text-green-300 relative`}
        onClick={() => setShowProfilesModal(true)}
        disabled={clientProfiles.length === 0}
        title={clientProfiles.length === 0 ? "El cliente no tiene perfiles asignados" : "Ver perfiles del cliente"}
      >
        <BrandWhatsapp size={16} className={clientProfiles.length === 0 ? "text-gray-500 cursor-not-allowed" : ""} />
        
        {/* Indicador si hay perfiles próximos a expirar */}
        {hasNearExpiryProfiles && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
        )}
        
        {/* Indicador si hay perfiles expirados */}
        {hasExpiredProfiles && (
          <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        )}
      </button>
      
      {showProfilesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <User size={20} className="text-green-400 mr-2" />
                Perfiles de {client.name}
                {client.type === 'revendedor' && (
                  <span className="ml-2 px-2 py-1 bg-purple-900 text-purple-300 text-xs rounded-full">Revendedor</span>
                )}
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setShowProfilesModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            {client.phone && (
              <div className="mb-4 bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                <p className="text-sm text-gray-300">
                  Teléfono: <span className="text-white">{client.phone}</span>
                </p>
                
                {/* Botones de notificación masiva mejorados con detección automática */}
                <div className="flex gap-2">
                  {hasExpiredProfiles && (
                    <button
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md flex items-center transition-colors"
                      onClick={() => sendDirectNotification(getExpiredProfiles(), 'expirado')}
                      title={`Enviar notificación de expiración a ${getExpiredProfiles().length} perfil(es)`}
                    >
                      <BrandWhatsapp size={14} className="mr-1" />
                      Expirados ({getExpiredProfiles().length})
                    </button>
                  )}
                  
                  {hasNearExpiryProfiles && (
                    <button
                      className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-md flex items-center transition-colors"
                      onClick={() => sendDirectNotification(getNearExpiryProfiles(), 'renovaciones')}
                      title={`Enviar notificación de renovación a ${getNearExpiryProfiles().length} perfil(es)`}
                    >
                      <BrandWhatsapp size={14} className="mr-1" />
                      Por Vencer ({getNearExpiryProfiles().length})
                    </button>
                  )}

                  {/* Botón para selección manual cuando hay perfiles que no están en las categorías automáticas */}
                  {clientProfiles.length > 0 && (
                    <button
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center transition-colors"
                      onClick={() => showNotificationTypeSelector(clientProfiles, 'client')}
                      title="Seleccionar manualmente el tipo de notificación"
                    >
                      <BrandWhatsapp size={14} className="mr-1" />
                      Personalizada
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {clientProfiles.length === 0 ? (
              <div className="bg-gray-700 p-8 rounded-lg text-center">
                <p className="text-gray-400">El cliente no tiene perfiles asignados.</p>
              </div>
            ) : (
              <div>
                {/* Acciones en grupo */}
                {clientProfiles.length > 1 && (
                  <div className="mb-4 flex items-center justify-between bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        checked={selectAll}
                        onChange={handleToggleSelectAll}
                        className="mr-2 h-4 w-4"
                      />
                      <label className="text-sm text-gray-300 mr-2">Seleccionar todos</label>
                      <span className="text-xs bg-gray-600 px-2 py-1 rounded">
                        {Object.values(selectedProfiles).filter(Boolean).length} seleccionados
                      </span>
                    </div>
                    
                    {Object.values(selectedProfiles).some(Boolean) && (
                      <button
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-md flex items-center transition-colors"
                        onClick={() => showNotificationTypeSelector(getSelectedProfiles(), 'client')}
                        title={`Seleccionar tipo de notificación para ${getSelectedProfiles().length} perfil(es)`}
                      >
                        <BrandWhatsapp size={14} className="mr-1" />
                        Notificar Seleccionados ({getSelectedProfiles().length})
                      </button>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {clientProfiles.map((profile, index) => {
                    // Determinar el estado del perfil para mostrar color adecuado
                    const isExpired = isProfileExpired(profile);
                    const isNearExpiry = isProfileNearExpiry(profile);
                    
                    let statusColor = 'bg-green-900 bg-opacity-30';
                    let textColor = 'text-green-400';
                    let borderColor = 'border-green-900';
                    
                    if (isExpired) {
                      statusColor = 'bg-red-900 bg-opacity-30';
                      textColor = 'text-red-400';
                      borderColor = 'border-red-900';
                    } else if (isNearExpiry) {
                      statusColor = 'bg-orange-900 bg-opacity-30';
                      textColor = 'text-orange-400';
                      borderColor = 'border-orange-900';
                    }
                    
                    return (
                      <div 
                        key={`${profile.accountId}-${profile.id}`} 
                        className={`${statusColor} border ${borderColor} rounded-lg p-4 relative`}
                      >
                        {/* Checkbox para selección múltiple */}
                        <div className="absolute top-2 right-2">
                          <input 
                            type="checkbox" 
                            checked={!!selectedProfiles[profile.id]}
                            onChange={() => handleToggleProfileSelection(profile.id)}
                            className="h-4 w-4"
                          />
                        </div>
                        
                        <div className="flex justify-between items-start mb-2">
                          <h3 className={`font-bold ${textColor}`}>{profile.name}</h3>
                          <div className="flex items-center text-xs">
                            <Clock size={14} className={textColor} />
                            <span className={`ml-1 ${textColor}`}>
                              {getDaysUntilExpiry(profile)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-sm space-y-2">
                          <p className="text-gray-300">
                            Servicio: <span className="text-white">{profile.serviceName}</span>
                          </p>
                          <p className="text-gray-300">
                            Cuenta: <span className="text-white">{profile.username}</span>
                          </p>
                          <p className="text-gray-300 flex items-center">
                            <Calendar size={14} className="mr-1" />
                            Vence: <span className="text-white ml-1">
                              {formatDate(profile.expiryDate || profile.expiry_date)}
                            </span>
                          </p>
                          {profile.pin && (
                            <p className="text-gray-300 flex items-center">
                              <Key size={14} className="mr-1" />
                              PIN: <span className="text-white ml-1">{profile.pin}</span>
                            </p>
                          )}
                          <p className="text-gray-300">
                            Precio: <span className="text-white">
                              {client.type === 'revendedor' ? (
                                <>
                                  <span className="line-through text-gray-400 mr-1">{formatCurrency(profile.servicePrice)}</span>
                                  {formatCurrency(profile.servicePrice * 0.8)}
                                </>
                              ) : (
                                formatCurrency(profile.servicePrice)
                              )}
                            </span>
                          </p>
                        </div>
                        
                        {/* Botón individual para notificación con detección automática */}
                        <button
                          className={`mt-3 inline-flex items-center justify-center w-full py-2 px-3 rounded-md transition-colors ${
                            isExpired ? 'bg-red-600 hover:bg-red-700' :
                            isNearExpiry ? 'bg-orange-600 hover:bg-orange-700' :
                            'bg-blue-600 hover:bg-blue-700'
                          } text-white text-sm`}
                          onClick={() => {
                            const detectedType = detectNotificationType([profile]);
                            if (detectedType === 'expirado' || detectedType === 'renovaciones') {
                              // Envío automático para tipos estándar
                              sendDirectNotification([profile], detectedType);
                            } else {
                              // Selección manual para otros casos
                              showNotificationTypeSelector([profile], 'client');
                            }
                          }}
                          title={`Enviar notificación ${
                            isExpired ? 'de expiración' :
                            isNearExpiry ? 'de renovación' :
                            'personalizada'
                          }`}
                        >
                          <BrandWhatsapp size={16} className="mr-1" />
                          {isExpired ? 'Notificar Expiración' :
                           isNearExpiry ? 'Notificar Renovación' :
                           'Notificación Personalizada'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para seleccionar tipo de notificación */}
      {showNotificationTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <BrandWhatsapp size={20} className="text-green-400 mr-2" />
                Seleccionar Tipo de Notificación
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setShowNotificationTypeModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-700 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-300 mb-2">
                  📊 <strong>Perfiles seleccionados:</strong> {profilesForNotification.length}
                </p>
                
                {profilesForNotification.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {profilesForNotification.map((profile, index) => {
                      const isExpired = isProfileExpired(profile);
                      const isNearExpiry = isProfileNearExpiry(profile);
                      
                      let badgeColor = 'bg-green-900 text-green-300';
                      let status = 'Activo';
                      
                      if (isExpired) {
                        badgeColor = 'bg-red-900 text-red-300';
                        status = 'Expirado';
                      } else if (isNearExpiry) {
                        badgeColor = 'bg-orange-900 text-orange-300';
                        status = 'Por vencer';
                      }
                      
                      return (
                        <span key={index} className={`text-xs px-2 py-1 rounded-full ${badgeColor}`}>
                          {profile.name} ({status})
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-300 mb-3">
                  💡 <strong>Tipo sugerido automáticamente:</strong> 
                  <span className="ml-2 px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded-full">
                    {getAvailableNotificationTypes('client').find(type => type.key === selectedNotificationType)?.label || 'Renovación'}
                  </span>
                </p>
              </div>
              
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-200 mb-2">Selecciona el tipo de notificación:</h3>
                
                {getAvailableNotificationTypes('client').map(({ key, label, color, description }) => (
                  <div key={key} className="relative">
                    <input
                      type="radio"
                      id={`notification-${key}`}
                      name="notificationType"
                      value={key}
                      checked={selectedNotificationType === key}
                      onChange={(e) => setSelectedNotificationType(e.target.value)}
                      className="sr-only"
                    />
                    <label
                      htmlFor={`notification-${key}`}
                      className={`block w-full p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedNotificationType === key
                          ? `${color} border-white text-white font-semibold shadow-lg transform scale-105`
                          : `bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500`
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-base mb-1">{label}</div>
                          <div className="text-sm opacity-80">{description}</div>
                        </div>
                        
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedNotificationType === key
                            ? 'border-white bg-white'
                            : 'border-gray-400'
                        }`}>
                          {selectedNotificationType === key && (
                            <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                          )}
                        </div>
                      </div>
                      
                      {/* Indicador de sugerencia automática */}
                      {selectedNotificationType === key && detectNotificationType(profilesForNotification) === key && (
                        <div className="mt-2 flex items-center text-xs">
                          <span className="px-2 py-1 bg-green-800 text-green-200 rounded-full">
                            ✨ Sugerido automáticamente
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                ))}
              </div>
              
              {/* Información adicional sobre tipos no disponibles */}
              <div className="mt-4 p-3 bg-blue-900 bg-opacity-30 rounded-lg">
                <p className="text-xs text-blue-300">
                  💡 <strong>Tip:</strong> Para notificaciones de "Cambio de Contraseña" y "Garantía/Soporte".
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
                onClick={() => setShowNotificationTypeModal(false)}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-md text-white flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={sendCustomNotification}
                disabled={!selectedNotificationType}
              >
                <BrandWhatsapp size={16} />
                Enviar Notificación
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientWhatsappButton;
